function findVariantById(variants, id) {
  return variants.find((variant) => String(variant.id) === String(id)) || null;
}

function findExactVariant(variants, selectedOptions) {
  return variants.find((variant) => (
    variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => value === selectedOptions[index])
  )) || null;
}

function chooseInitialVariant(variants, selectedVariantId, fallbackVariantId, namedOptionIndexes) {
  const explicitlySelected = selectedVariantId && findVariantById(variants, selectedVariantId);
  if (explicitlySelected) return explicitlySelected;

  const serverFallback = fallbackVariantId && findVariantById(variants, fallbackVariantId);
  if (serverFallback) return serverFallback;

  const [packageIndex, conditionIndex] = namedOptionIndexes;
  if (packageIndex >= 0 && conditionIndex >= 0) {
    const approvedDefault = variants.find((variant) => (
      variant.available
        && variant.options[packageIndex] === 'Drone Only (No Battery)'
        && variant.options[conditionIndex] === 'Flawless'
    ));
    if (approvedDefault) return approvedDefault;
  }

  return variants[0] || null;
}

function resolveChangedVariant(variants, selectedOptions, changedIndex, currentVariantId) {
  const exact = findExactVariant(variants, selectedOptions);
  if (exact) return exact;

  const changedValue = selectedOptions[changedIndex];
  const availableMatch = variants.find((variant) => (
    variant.available && variant.options[changedIndex] === changedValue
  ));
  if (availableMatch) return availableMatch;

  const anyMatch = variants.find((variant) => variant.options[changedIndex] === changedValue);
  return anyMatch || findVariantById(variants, currentVariantId) || variants[0] || null;
}

function parseJson(root, selector) {
  const element = root.querySelector(selector);
  if (!element) return [];
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    console.error('Air 3 hero data could not be read', error);
    return [];
  }
}

function initializeAir3Hero(root) {
  if (root.dataset.rhHeroReady === 'true') return;
  root.dataset.rhHeroReady = 'true';

  const variants = parseJson(root, '[data-rh-air3-variants]');
  const images = parseJson(root, '[data-rh-air3-images]');
  if (!variants.length) return;

  const packageIndex = Number(root.dataset.packageIndex);
  const conditionIndex = Number(root.dataset.conditionIndex);
  let currentVariant = chooseInitialVariant(
    variants,
    root.dataset.selectedVariantId,
    root.dataset.fallbackVariantId,
    [packageIndex, conditionIndex],
  );
  let photoIndex = Math.max(0, Number(root.dataset.initialPhotoIndex) || 0);

  const one = (selector) => root.querySelector(selector);
  const all = (selector) => Array.from(root.querySelectorAll(selector));
  const setAttributeIfChanged = (element, name, value) => {
    const next = String(value);
    if (element.getAttribute(name) !== next) element.setAttribute(name, next);
  };
  const setOptionalAttributeIfChanged = (element, name, value) => {
    if (value) setAttributeIfChanged(element, name, value);
    else if (element.hasAttribute(name)) element.removeAttribute(name);
  };
  const productForm = one(`#product-form-${root.dataset.productId}`);
  const controls = one('[data-rh-air3-controls]');
  const formGroup = productForm?.querySelector('.productView-group');
  if (productForm && controls && formGroup) {
    while (controls.firstChild) productForm.insertBefore(controls.firstChild, formGroup);
    controls.remove();
  }

  const ddpRow = one('.approved-ddp-row');
  const assurances = one('.rh-purchase-assurances');
  const addButton = formGroup?.querySelector('#product-add-to-cart');
  const nativeCheckout = formGroup?.querySelector('.productView-payment');
  if (productForm && formGroup && addButton) productForm.insertBefore(addButton, formGroup);
  if (productForm && formGroup && nativeCheckout) productForm.insertBefore(nativeCheckout, formGroup);
  if (productForm && formGroup && ddpRow) productForm.insertBefore(ddpRow, formGroup);
  if (ddpRow && assurances) ddpRow.prepend(assurances);

  const productView = root.closest('.productView');
  const stickySelector = productView?.querySelector('variant-sticky-selects select');

  function setPhoto(index) {
    const image = images[index];
    const heroImage = one('#hero-image');
    if (!image || !heroImage) return;
    photoIndex = index;
    setOptionalAttributeIfChanged(heroImage, 'sizes', image.sizes);
    setOptionalAttributeIfChanged(heroImage, 'srcset', image.srcset);
    setAttributeIfChanged(heroImage, 'src', image.src);
    setAttributeIfChanged(heroImage, 'alt', image.alt || '');
    const stage = one('#stage-photo');
    stage?.classList.toggle('show-alt', index > 0 && index < 6);
    stage?.classList.toggle('show-package', index >= 6);
    const counter = one('#gallery-counter');
    if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(images.length).padStart(2, '0')}`;
    all('[data-approved-photo]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.approvedPhoto) === index));
    });
  }

  function syncNativeForm(variant) {
    const formIds = [
      `product-form-${root.dataset.productId}`,
      `product-form-installment-${root.dataset.productId}`,
      `product-form-sticky-${root.dataset.productId}`,
    ];
    formIds.forEach((id) => {
      const form = document.getElementById(id);
      const input = form?.querySelector('input[name="id"]');
      if (!input) return;
      const nextId = String(variant.id);
      if (String(input.value) === nextId) return;
      input.value = nextId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (stickySelector && String(stickySelector.value) !== String(variant.id)) {
      stickySelector.value = String(variant.id);
      stickySelector.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const submitButtons = all('[name="add"]');
    submitButtons.forEach((button) => {
      const disabled = !variant.available;
      if (button.disabled !== disabled) button.disabled = disabled;
      setAttributeIfChanged(button, 'aria-disabled', disabled);
      const inventoryQuantity = String(variant.inventory_quantity ?? 0);
      if (button.dataset.inventoryQuantity !== inventoryQuantity) button.dataset.inventoryQuantity = inventoryQuantity;
      const available = String(Boolean(variant.available && variant.inventory_management && Number(variant.inventory_quantity) <= 0));
      if (button.dataset.available !== available) button.dataset.available = available;
      if (variant.button_label && button.textContent !== variant.button_label) button.textContent = variant.button_label;
    });
  }

  function updateSelectedControls(variant) {
    all('[data-option-index][data-option-value]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      setAttributeIfChanged(button, 'aria-pressed', variant.options[optionIndex] === button.dataset.optionValue);
    });
    const packageNote = one('#package-note');
    const selectedPackage = packageIndex >= 0 ? variant.options[packageIndex] : '';
    const note = all('[data-package-note]').find((element) => element.dataset.packageNote === selectedPackage);
    if (packageNote && note) packageNote.textContent = note.textContent;
  }

  function updateVariant(variant, changePhoto) {
    if (!variant) return;
    currentVariant = variant;
    updateSelectedControls(variant);
    const price = one('#product-price');
    if (price) price.textContent = variant.formatted_price;
    const compareLine = one('[data-rh-compare-line]');
    if (compareLine) {
      const discounted = Number(variant.compare_at_price) > Number(variant.price);
      compareLine.hidden = !discounted;
      const oldPrice = one('[data-rh-compare-price]');
      const discount = one('[data-rh-discount]');
      if (oldPrice) oldPrice.textContent = discounted ? variant.formatted_compare_at_price : '';
      if (discount) discount.textContent = discounted ? Math.round((1 - variant.price / variant.compare_at_price) * 100) + (root.querySelector('[data-rh-ui="discountSuffix"]')?.textContent || '% OFF') : '';
    }
    const stagePrice = one('#stage-price');
    if (stagePrice?.firstChild) stagePrice.firstChild.textContent = `${variant.formatted_price} `;
    const currency = one('[data-rh-currency]');
    if (currency) currency.textContent = variant.currency;
    syncNativeForm(variant);

    if (changePhoto && variant.image_id) {
      const variantPhotoIndex = images.findIndex((image) => String(image.id) === String(variant.image_id));
      if (variantPhotoIndex >= 0) setPhoto(variantPhotoIndex);
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') !== String(variant.id)) {
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  all('[data-option-index][data-option-value]').forEach((button) => {
    button.addEventListener('click', () => {
      const changedIndex = Number(button.dataset.optionIndex);
      const selectedOptions = currentVariant.options.slice();
      selectedOptions[changedIndex] = button.dataset.optionValue;
      updateVariant(resolveChangedVariant(variants, selectedOptions, changedIndex, currentVariant.id), true);
    });
  });

  stickySelector?.addEventListener('change', () => {
    const stickyVariant = findVariantById(variants, stickySelector.value);
    if (stickyVariant && String(stickyVariant.id) !== String(currentVariant.id)) {
      updateVariant(stickyVariant, true);
    }
  });

  all('[data-approved-photo]').forEach((button) => {
    button.addEventListener('click', () => setPhoto(Number(button.dataset.approvedPhoto)));
  });

  one('#zoom-product')?.addEventListener('click', () => {
    const image = images[photoIndex];
    if (!image) return;
    const content = document.createElement('div');
    const expandedImage = document.createElement('img');
    const caption = document.createElement('p');
    expandedImage.src = image.src;
    expandedImage.alt = image.alt || '';
    caption.textContent = one('.photo-caption')?.textContent || '';
    content.append(expandedImage, caption);
    document.dispatchEvent(new CustomEvent('rh:preview', { detail: { title: expandedImage.alt, content } }));
  });

  const stickyGallery = one('.approved-gallery-sticky');
  const mobileGalleryIsStatic = window.matchMedia?.('(max-width: 749px)').matches === true;
  if (stickyGallery && !mobileGalleryIsStatic && typeof ResizeObserver !== 'undefined') {
    const sizeStickyGallery = () => {
      const header = document.querySelector('sticky-header');
      const headerHeight = header ? Math.min(header.getBoundingClientRect().height, 180) : 80;
      const inset = Math.min(headerHeight + 12, window.innerHeight - stickyGallery.offsetHeight - 16);
      stickyGallery.style.setProperty('--gallery-sticky-top', `${inset}px`);
    };
    new ResizeObserver(sizeStickyGallery).observe(stickyGallery);
    window.addEventListener('resize', sizeStickyGallery, { passive: true });
    sizeStickyGallery();
  }

  setPhoto(Math.min(photoIndex, Math.max(images.length - 1, 0)));
  updateVariant(currentVariant, false);
}

if (typeof document !== 'undefined') {
  const initializeAll = () => document.querySelectorAll('[data-rh-air3-hero]').forEach(initializeAir3Hero);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { chooseInitialVariant, findExactVariant, resolveChangedVariant };
}

